import api from './authService'

export interface PaymentResponse {
  success: boolean
  data?: any
  message?: string
  bypass?: boolean
  order_id?: string | null
  amount?: number
  currency?: string
  key_id?: string
}

export interface BillingDetails {
  billingState: string
  billingPan: string
  billingGstin?: string
  billingAddress: string
}

export interface GstBreakdown {
  originalPrice: number
  discountPercent: number
  discountAmount: number
  priceAfterDiscount: number
  taxAmount: number
  cgst: number
  sgst: number
  igst: number
  finalInclTax: number
  supplyType: 'B2B' | 'B2C'
}

/** Compute GST breakdown — 18% on post-discount price */
export function computeGst(
  basePrice: number,
  discountPercent: number,
  billingState: string,
  billingGstin?: string
): GstBreakdown {
  const GST_RATE = 0.18
  const discountAmount = basePrice * (discountPercent / 100)
  const priceAfterDiscount = basePrice - discountAmount
  const taxAmount = priceAfterDiscount * GST_RATE
  const isTN = billingState.toLowerCase() === 'tamil nadu'
  const cgst = isTN ? taxAmount / 2 : 0
  const sgst = isTN ? taxAmount / 2 : 0
  const igst = isTN ? 0 : taxAmount
  const finalInclTax = priceAfterDiscount + taxAmount
  const supplyType: 'B2B' | 'B2C' = billingGstin ? 'B2B' : 'B2C'
  return {
    originalPrice: basePrice,
    discountPercent,
    discountAmount,
    priceAfterDiscount,
    taxAmount,
    cgst,
    sgst,
    igst,
    finalInclTax,
    supplyType,
  }
}

export const paymentService = {
  async updateSubscriptionStatus(status: 'F' | 'P' | 'S'): Promise<PaymentResponse> {
    const response = await api.post('/payment/update-subscription', { subscribed: status })
    return response.data
  },

  async upgradePlan(
    plan: 'starter' | 'intelligence',
    razorpayPaymentId?: string,
    couponCode?: string,
    billing?: BillingDetails
  ): Promise<PaymentResponse> {
    const response = await api.post('/payment/upgrade-plan', {
      plan,
      razorpay_payment_id: razorpayPaymentId,
      couponCode,
      billingState: billing?.billingState,
      billingPan: billing?.billingPan,
      billingGstin: billing?.billingGstin,
      billingAddress: billing?.billingAddress,
    })
    return response.data
  },

  async createOrder(
    plan: 'starter' | 'intelligence' | 'topup',
    couponCode?: string,
    billing?: BillingDetails
  ): Promise<PaymentResponse> {
    const response = await api.post('/payment/create-order', {
      plan,
      couponCode,
      billingState: billing?.billingState,
      billingPan: billing?.billingPan,
      billingGstin: billing?.billingGstin,
      billingAddress: billing?.billingAddress,
    })
    return response.data
  },

  async verifyPayment(
    razorpayPaymentId: string,
    razorpayOrderId: string,
    razorpaySignature: string
  ): Promise<PaymentResponse> {
    const response = await api.post('/payment/verify-payment', {
      razorpay_payment_id: razorpayPaymentId,
      razorpay_order_id: razorpayOrderId,
      razorpay_signature: razorpaySignature,
    })
    return response.data
  },

  async purchaseTopup(razorpayPaymentId?: string, couponCode?: string): Promise<PaymentResponse> {
    const response = await api.post('/payment/purchase-topup', { razorpay_payment_id: razorpayPaymentId, couponCode })
    return response.data
  },

  async validateCoupon(couponCode: string, plan: 'starter' | 'intelligence'): Promise<PaymentResponse> {
    const response = await api.post('/payment/validate-coupon', { couponCode, plan })
    return response.data
  },

  async billingStatus(): Promise<PaymentResponse> {
    const response = await api.get('/payment/billing-status')
    return response.data
  },

  async getCheckoutDetails(ip?: string): Promise<PaymentResponse> {
    const response = await api.get('/payment/checkout-details', { params: { ip } })
    return response.data
  },
}

export default paymentService
